/**
 * SpreadsheetColumnPicker.tsx - SpreadsheetColumnPicker - Show/hide columns
 * 
 * CHANGE HISTORY:
 * - 251021_0421 [7529d5e] danielbeer: feat: migrate all packages to TypeScript structure
 */
import React, { useState, useEffect, useRef } from 'react';
import './SpreadsheetColumnPicker.css';

export const SpreadsheetColumnPicker = ({ gridRef, columnApi, onClose }) => {
  const [columns, setColumns] = useState([]);
  const dropdownRef = useRef(null);
  
  // Load columns
  useEffect(() => {
    if (!columnApi) return;
    
    const allColumns = columnApi.getAllColumns();
    const columnList = allColumns
      .filter(col => col.getColDef().headerName) // Skip auto-group columns
      .map(col => ({
        field: col.getColId(),
        headerName: col.getColDef().headerName,
        visible: col.isVisible(),
      }));
    
    setColumns(columnList);
  }, [columnApi]);
  
  // Close on click outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        onClose();
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [onClose]);
  
  // Toggle column visibility
  const toggleColumn = (field) => {
    if (!columnApi) return;
    
    const column = columns.find(col => col.field === field);
    if (!column) return;
    
    const newVisible = !column.visible;
    columnApi.setColumnVisible(field, newVisible);
    
    // Update state
    setColumns(prev => prev.map(col =>
      col.field === field ? { ...col, visible: newVisible } : col
    ));
  };
  
  // Show all columns
  const showAll = () => {
    if (!columnApi) return;
    
    columns.forEach(col => {
      columnApi.setColumnVisible(col.field, true);
    });
    
    setColumns(prev => prev.map(col => ({ ...col, visible: true })));
  };
  
  // Hide all columns
  const hideAll = () => {
    if (!columnApi) return;
    
    columns.forEach(col => {
      columnApi.setColumnVisible(col.field, false);
    });
    
    setColumns(prev => prev.map(col => ({ ...col, visible: false })));
  };
  
  return (
    <div className="griddr-column-picker" ref={dropdownRef}>
      <div className="griddr-column-picker-header">
        <span className="griddr-column-picker-title">Show/Hide Columns</span>
        <button
          className="griddr-column-picker-close"
          onClick={onClose}
          title="Close"
        >
          ✕
        </button>
      </div>
      
      <div className="griddr-column-picker-actions">
        <button
          className="griddr-column-picker-action"
          onClick={showAll}
        >
          Show All
        </button>
        <button
          className="griddr-column-picker-action"
          onClick={hideAll}
        >
          Hide All
        </button>
      </div>
      
      <div className="griddr-column-picker-list">
        {columns.map(col => (
          <label
            key={col.field}
            className="griddr-column-picker-item"
          >
            <input
              type="checkbox"
              checked={col.visible}
              onChange={() => toggleColumn(col.field)}
            />
            <span className="griddr-column-picker-label">
              {col.headerName}
            </span>
          </label>
        ))}
      </div>
    </div>
  );
};

export default SpreadsheetColumnPicker;
